import { z } from 'zod';

export const matchedObjectTypeSchema = z.enum([
  'BILL', 'INVOICE', 'RECEIPT', 'EXPENSE', 'TRANSFER', 'PAYOUT',
  'PAYROLL', 'TAX_PAYMENT', 'UNKNOWN',
]);
export const matchStatusSchema = z.enum(['SUGGESTED', 'AUTO_MATCHED', 'CONFIRMED', 'REJECTED']);
export const matchReasonSchema = z.enum([
  'EXACT_REFERENCE', 'AMOUNT_DATE', 'MERCHANT_AMOUNT', 'MANY_TO_ONE',
  'TRANSFER_DETECTED', 'MANUAL', 'AI_SUGGESTED', 'EMBEDDING_SIMILARITY',
]);
export const clientRuleTypeSchema = z.enum([
  'MERCHANT_CATEGORY', 'MERCHANT_ACCOUNT', 'RECONCILIATION',
  'PATTERN', 'TRANSFER', 'PAYOUT',
]);

export const runReconciliationBody = z.discriminatedUnion('scope', [
  z.object({
    organizationId: z.string().cuid(),
    scope: z.literal('monthly-close'),
    monthlyCloseId: z.string().cuid(),
  }),
  z.object({
    organizationId: z.string().cuid(),
    scope: z.literal('transaction'),
    transactionId: z.string().cuid(),
  }),
  z.object({
    organizationId: z.string().cuid(),
    scope: z.literal('org-since'),
    since: z.string().datetime(),
  }),
]);

export const queueQuerySchema = z.object({
  organizationId: z.string().cuid(),
  group: z
    .enum([
      'all',
      'unmatched',
      'suggested',
      'auto',
      'missing-doc',
      'duplicates',
      'transfers',
      'ambiguous-vendor',
      'amount-mismatch',
      'multiple-candidates',
    ])
    .optional()
    .default('all'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const allocationSchema = z.object({
  objectType: matchedObjectTypeSchema,
  objectId: z.string(),
  amount: z.string().regex(/^-?\d+(\.\d{1,2})?$/),
});

export const adjustmentTypeSchema = z.enum([
  'PROCESSING_FEE',
  'BANK_FEE',
  'FX_DIFFERENCE',
  'DISCOUNT',
  'WRITE_OFF',
  'INTEREST_EARNED',
  'ROUNDING',
  'OTHER',
]);

export const adjustmentSchema = z.object({
  type: adjustmentTypeSchema,
  amount: z.string().regex(/^-?\d+(\.\d{1,2})?$/),
  description: z.string().max(500).optional(),
});

export const createMatchBody = z.object({
  transactionId: z.string().cuid(),
  matchedObjectType: matchedObjectTypeSchema,
  matchedObjectId: z.string().optional(),
  allocations: z.array(allocationSchema).optional(),
  notes: z.string().max(1000).optional(),
  /** Optional variance adjustment captured at create time. */
  adjustment: adjustmentSchema.optional(),
  /**
   * When `true` (default `false`), the match lands as SUGGESTED instead of
   * CONFIRMED. SMEs use this to propose a match-to-existing entity for
   * reviewer approval. Reviewer + PLATFORM_ADMIN may pass either, but the
   * route forces SUGGESTED for CLIENT on non-document object types
   * regardless of this flag.
   */
  propose: z.boolean().optional(),
});

export const confirmMatchBody = z.object({
  notes: z.string().max(1000).optional(),
});

export const rejectMatchBody = z.object({
  reason: z.string().min(1).max(500),
});

export const bulkConfirmBody = z.object({
  matchIds: z.array(z.string().cuid()).min(1).max(200),
});

export const createRuleBody = z.object({
  organizationId: z.string().cuid(),
  ruleType: clientRuleTypeSchema,
  merchantId: z.string().cuid().optional(),
  patternText: z.string().optional(),
  categoryId: z.string().cuid().optional(),
  accountCode: z.string().optional(),
  reconciliationBehavior: z.record(z.string(), z.unknown()).optional(),
  notes: z.string().max(1000).optional(),
  dryRun: z.boolean().optional().default(false),
});
